import { zodResolver } from '@hookform/resolvers/zod';
import * as m from '@staiter/i18n';
import { ProjectUpsertInput } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { CalendarClock, FileText, Globe, Lock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PageHeader, Skeleton } from '~/components/data';
import { CounterpartyPicker } from '~/components/domain/CounterpartyPicker';
import { SalesOrderPicker } from '~/components/domain/SalesOrderPicker';
import { emitToast } from '~/components/feedback';
import {
  Button,
  ConfirmDialog,
  DatePicker,
  Input,
  Select,
  Textarea,
} from '~/components/primitives';
import { VisibilityChangeDialog } from '~/components/tasks/dialogs/VisibilityChangeDialog';
import { ApiRequestError, apiRequest } from '~/lib/api/client';
import {
  changeProjectVisibility,
  fetchProject,
  moveProjectEntity,
  updateProject,
} from '~/lib/api/projects';
import { buildProjectUpdateBody } from '~/lib/projects/update-body';
import { useActiveEntityStore } from '~/store/active-entity';
import own from './_auth.projects.$id.edit.module.css';
import styles from './_auth.sales-orders.new.module.css';

/**
 * G16.2 — Project edit route.
 * Sprint M+ FE-12: primitives + sticky footer + Cancel + DisabledWithReason
 * для locked-after-active полей (customer_id, sales_order_id когда stage≠planning).
 */
/**
 * CM-F3 (D4.11a) — якорь поля из меню проекта. «Переименовать» и «Перенести в
 * другую компанию…» приводили в форму без указания, что именно менять: оба
 * действия живут ЗДЕСЬ, но форма длинная, и нужное поле искали глазами.
 */
const editSearchSchema = z.object({
  focus: z.enum(['name', 'entity']).optional().catch(undefined),
});

export const Route = createFileRoute('/_auth/projects/$id/edit')({
  validateSearch: editSearchSchema,
  component: ProjectEditPage,
});

type ProjectTypeValue = 'supply' | 'manufacturing' | 'rnd' | 'modernization' | 'other';

type FormData = {
  name: string;
  customer_id: string;
  sales_order_id?: string | null;
  project_type?: ProjectTypeValue | null;
  expected_start_date?: string | null;
  expected_end_date?: string | null;
  estimated_cost?: { amount: string; currency: 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY' };
  pm_user_id?: string | null;
  notes?: string | null;
};

function ProjectEditPage(): JSX.Element {
  const { id } = Route.useParams();
  const { focus } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const projectQuery = useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProject(id),
  });

  // access-go-live — перенос проекта между компаниями (D9/D10 backlog project-move-entity):
  // Select при >1 доступной компании; отдельная операция move-entity (не PATCH-поле).
  const memberships = useActiveEntityStore((s) => s.memberships);
  const companyOptions = [
    ...new Map(memberships.map((mb) => [mb.entity_id, mb.entity_name])).entries(),
  ].map(([value, label]) => ({ value, label }));
  const showCompanySelect = companyOptions.length > 1;
  const [targetEntityId, setTargetEntityId] = useState<string | null>(null);
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  const pendingDataRef = useRef<FormData | null>(null);
  const effectiveTarget = targetEntityId ?? projectQuery.data?.entity_id ?? null;
  const entityChanged =
    !!projectQuery.data && !!effectiveTarget && effectiveTarget !== projectQuery.data.entity_id;

  /**
   * CM-F3 (D4.11a) — довести до поля, а не до формы. Фокус, а не только прокрутка:
   * «Переименовать» — это намерение печатать, и курсор обязан уже стоять в поле.
   * Поиск по `id` вместо ref-а: контролы приходят из примитивов и RHF, и вплетать
   * во внутренний `ref` мимо `register` — лишний риск ради того же результата.
   */
  useEffect(() => {
    if (!focus || projectQuery.isLoading) return;
    const target =
      focus === 'name'
        ? document.getElementById('project-name')
        : document.getElementById('project-entity');
    if (!target) return;
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const focusable =
      target instanceof HTMLInputElement ? target : target.querySelector('input, select, button');
    if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
  }, [focus, projectQuery.isLoading]);

  const form = useForm<FormData>({
    resolver: zodResolver(ProjectUpsertInput.partial().required({ name: true, customer_id: true })),
    mode: 'onChange',
    defaultValues: {
      name: '',
      customer_id: '',
      sales_order_id: null,
      project_type: null,
      expected_start_date: null,
      expected_end_date: null,
      estimated_cost: { amount: '0', currency: 'KZT' },
      pm_user_id: null,
      notes: null,
    },
  });

  useEffect(() => {
    const p = projectQuery.data;
    if (!p) return;
    form.reset({
      name: p.name,
      customer_id: p.customer_id ?? undefined,
      sales_order_id: p.sales_order_id,
      project_type: p.project_type as ProjectTypeValue | null,
      expected_start_date: p.expected_start_date,
      expected_end_date: p.expected_end_date,
      estimated_cost: p.estimated_cost,
      pm_user_id: p.pm_user_id,
      notes: p.notes,
    });
  }, [projectQuery.data, form]);

  const lockedAfterActive = projectQuery.data && (projectQuery.data.stage as string) !== 'planning';

  /**
   * F2 (project-plan-change §4.2) — одна сборка тела на оба пути.
   *
   * Гвард B1 старое тело терпит (сравнивает значения, а не присутствие ключа), но
   * отправлять то, чего не меняешь, — заявка на отказ при первом же расхождении
   * форматов дат. Симметрия, которой не хватало с самого начала: клиент с заказом
   * исключались, даты — нет.
   */
  const buildUpdateBody = (data: FormData) => buildProjectUpdateBody(data, !!lockedAfterActive);

  const mutation = useMutation({
    mutationFn: (data: FormData) => updateProject(id, buildUpdateBody(data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', id] });
      navigate({ to: '/projects/$id', params: { id } });
    },
    // FE-63 (Sprint 9) — same envelope handler как в create form
    onError: (err: unknown) => {
      if (err instanceof ApiRequestError && err.code === 'project_update.validation_failed') {
        const details = err.details as
          | { field_errors?: Array<{ field: string; code: string }> }
          | undefined;
        const fieldErrors = details?.field_errors ?? [];
        for (const fe of fieldErrors) {
          form.setError(fe.field as keyof FormData, {
            type: 'server',
            message: fe.code,
          });
        }
      }
    },
  });

  // access-go-live — сохранение + перенос: PATCH полей под текущей компанией, затем
  // move-entity (D4: detach клиента/заказа — подтверждён диалогом; D5-guard-ы тостятся
  // глобально через error_by_code). Порядок безопасен: упавший move оставляет проект
  // в старой компании с сохранёнными полями.
  const moveMutation = useMutation({
    mutationFn: async (data: FormData) => {
      // F2 — та же сборка тела, что и у обычного сохранения. Два списка ключей
      // разъезжаются молча: правку вносят в один, а второй остаётся прежним.
      await updateProject(id, buildUpdateBody(data));
      if (!effectiveTarget) throw new Error('no target entity');
      return moveProjectEntity(id, {
        target_entity_id: effectiveTarget,
        detach_financial_refs: true,
      });
    },
    onSuccess: () => {
      const companyName = companyOptions.find((o) => o.value === effectiveTarget)?.label ?? '';
      emitToast({ variant: 'success', title: m.project_move_success({ company: companyName }) });
      setMoveConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ['project', id] });
      // D10 — single-scope: контекст → компания проекта, навигация в список (detail
      // под старым контекстом сразу словил бы смену кэша/увод по stale-UUID).
      const store = useActiveEntityStore.getState();
      if (
        store.activeScope === 'entity' &&
        effectiveTarget &&
        store.activeEntityId &&
        effectiveTarget !== store.activeEntityId
      ) {
        store.setActiveEntity(effectiveTarget);
        navigate({ to: '/projects' });
        return;
      }
      navigate({ to: '/projects/$id', params: { id } });
    },
    onError: () => {
      // Guard-тост (has_invoices / has_wo_worklogs / ENTITY_FORBIDDEN) уже показан
      // глобальным MutationCache-обработчиком — здесь только закрываем диалог.
      setMoveConfirmOpen(false);
    },
  });

  // project-visibility (F18, §3.8) — счётчик участников закрытого проекта: сколько
  // людей его видят + переход к управлению (member-CRUD живёт в настройках, не здесь).
  const membersCountQuery = useQuery({
    queryKey: ['project', id, 'members-count'],
    queryFn: () =>
      apiRequest(
        `/task-module/projects/${id}/members`,
        z.object({ members: z.array(z.unknown()) }).transform((d) => d.members.length),
      ),
    enabled: projectQuery.data?.visibility === 'private',
    staleTime: 30_000,
  });

  // project-visibility (F2) — смена видимости в edit-форме идёт не «тихим полем»,
  // а транзакцией через member-review диалог F3 (тот же путь, что и F4-настройки).
  const [visibilityTarget, setVisibilityTarget] = useState<'open' | 'private' | null>(null);
  const visibilityMut = useMutation({
    mutationFn: (body: { visibility: 'open' | 'private'; exclude_user_ids: string[] }) =>
      changeProjectVisibility(id, body),
    onSuccess: () => {
      setVisibilityTarget(null);
      emitToast({ variant: 'success', title: m.tm_visibility_toast_changed() });
      qc.invalidateQueries({ queryKey: ['project', id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err) => {
      // 409 inbox_cannot_be_private и прочие — локализованный тост через error_by_code (F11).
      setVisibilityTarget(null);
      emitToast({
        variant: 'error',
        title: err instanceof ApiRequestError ? m.error_by_code(err.code) : m.error_toast_title(),
      });
    },
  });

  // PC-24: skeleton loading state
  if (projectQuery.isLoading)
    return (
      <div className={own.skeleton}>
        <Skeleton width={300} height={24} />
        <Skeleton height={56} />
        <Skeleton height={56} />
        <Skeleton height={56} />
        <Skeleton height={120} />
      </div>
    );
  if (!projectQuery.data) return <div>Not found</div>;

  const { register, formState, watch, setValue, handleSubmit } = form;
  const watched = watch();
  // FE-66 (Sprint 9) — removed per-field `lockedHint` prop usage в favor of single banner

  /**
   * F1 (project-plan-change §4.1) — баннер зафиксированного говорит правду.
   *
   * Было одно утверждение на три поля: «клиент, заказ и даты нельзя изменить пока
   * проект в работе». Сервер при этом менял все три по первому запросу, а рядом
   * стояла кнопка «Написать PM», открывавшая `mailto:pm@staiter.local` — константу
   * в коде, одну на все проекты и компании; домена `.local` в интернете нет.
   *
   * Стало два утверждения с РАЗНЫМИ следствиями:
   *   • план  — переносится с обоснованием, действие живёт в меню проекта;
   *   • контракт — задан заказом, и ссылка ведёт к нему, а не в письмо в никуда.
   *
   * Кнопка удалена, не переименована: за ней нет адресата.
   */
  const salesOrderId = projectQuery.data?.sales_order_id ?? null;
  const lockedBanner = lockedAfterActive ? (
    <div className={own.fixedBanner}>
      {/* Дизайн-ревью фазы A (UX-3): условность замка потерялась вместе со старым
          текстом. «Пока проект в работе» — не украшение: без него человек не знает,
          временное это состояние или навсегда. */}
      <p className={own.fixedLead} id="project-locked-lead">
        {m.project_fixed_lead()}
      </p>
      <div className={own.fixedRow} id="project-locked-plan">
        <span className={own.fixedIcon} aria-hidden="true">
          <CalendarClock size={14} />
        </span>
        <span className={own.fixedText}>
          <span className={own.fixedTitle}>{m.project_fixed_plan_title()}</span> —{' '}
          {m.project_fixed_plan_body()}{' '}
          {/* F5 — ссылка на само действие. До неё правило висело без выхода, и
              дизайн-гейт фазы A назвал это блокером: «Прежний тупик („написать PM“)
              заменён на более вежливый тупик». Диалог живёт на карточке вместе с
              превью и подписью — сюда его не дублируем, ведём якорем. */}
          <Link
            to="/projects/$id"
            params={{ id }}
            search={{ action: 'plan_shift' } as never}
            className={own.orderLink}
          >
            {m.project_fixed_plan_link()}
          </Link>
        </span>
      </div>
      <div className={own.fixedRow} id="project-locked-contract">
        <span className={own.fixedIcon} aria-hidden="true">
          <FileText size={14} />
        </span>
        <span className={own.fixedText}>
          <span className={own.fixedTitle}>{m.project_fixed_contract_title()}</span> —{' '}
          {salesOrderId ? (
            <>
              {m.project_fixed_contract_body_with_order()}{' '}
              <Link to="/sales-orders/$id" params={{ id: salesOrderId }} className={own.orderLink}>
                {projectQuery.data?.sales_order_code ?? salesOrderId}
              </Link>{' '}
              {m.project_fixed_contract_tail()}
            </>
          ) : (
            m.project_fixed_contract_body_no_order()
          )}
        </span>
      </div>
    </div>
  ) : null;

  // FE-67 (Sprint 9) — same enum-select pattern as create form
  const projectTypeOptions = [
    { value: 'supply', label: m.project_type_supply() },
    { value: 'manufacturing', label: m.project_type_manufacturing() },
    { value: 'rnd', label: m.project_type_rnd() },
    { value: 'modernization', label: m.project_type_modernization() },
    { value: 'other', label: m.project_type_other() },
  ];

  return (
    <div className={styles.page}>
      {/* FE-110 (Sprint 12 polish from S9B1-4): H1 несёт role/status, не дубль name.
          H1 = «Редактирование проекта», description = name + code + stage badge. */}
      <PageHeader
        title={m.project_edit_page_title()}
        description={
          <span className={own.headerMeta}>
            <strong className={own.headerName}>{projectQuery.data.name}</strong>
            <span className={own.headerCode}>{projectQuery.data.code}</span>
            {lockedAfterActive && <span className={own.stagePill}>{m.ui_v_rabote()}</span>}
          </span>
        }
      />
      <form
        onSubmit={handleSubmit((data) => {
          // access-go-live — смена компании требует подтверждения (перенос с каскадом).
          if (entityChanged) {
            pendingDataRef.current = data;
            setMoveConfirmOpen(true);
            return;
          }
          mutation.mutate(data);
        })}
        noValidate
        className={styles.form}
      >
        <Input id="project-name" label={m.project_form_name()} required {...register('name')} />
        {/* project-visibility (F2) — текущий статус + кнопка перехода через F3-диалог.
            Не react-hook-form-поле: переход — транзакция с побочными действиями (§2.3, §3.1).
            Surface-row с иконкой состояния (Globe/Lock) — статус = иконка+цвет+текст. */}
        <div className={own.visibilityRow}>
          <div className={own.visibilityInfo}>
            <span className={own.sectionCaption}>{m.tm_visibility_section_title()}</span>
            <span className={own.visibilityCurrent}>
              {projectQuery.data.visibility === 'private' ? (
                <Lock size={14} aria-hidden="true" />
              ) : (
                <Globe size={14} aria-hidden="true" />
              )}
              {projectQuery.data.visibility === 'private'
                ? m.tm_visibility_current_private()
                : m.tm_visibility_current_open()}
            </span>
            {/* project-visibility (F18, §3.8) — кто видит закрытый проект + переход
                к управлению участниками (полный CRUD — в настройках воркспейса). */}
            {projectQuery.data.visibility === 'private' && (
              <span className={own.membersRow}>
                <span className={own.membersCount}>
                  {m.project_members_count({ count: membersCountQuery.data ?? '…' })}
                </span>
                {/* design-review F18: link = text-primary + underline (SSOT), не второй
                    голубой акцент рядом с золотой CTA. */}
                <Link
                  to="/work-tasks/$projectId"
                  params={{ projectId: id }}
                  search={{ vt: 'settings' }}
                  className={own.membersLink}
                >
                  {m.project_members_manage_link()}
                </Link>
              </span>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() =>
              setVisibilityTarget(projectQuery.data?.visibility === 'private' ? 'open' : 'private')
            }
          >
            {projectQuery.data.visibility === 'private'
              ? m.tm_visibility_make_open()
              : m.tm_visibility_make_private()}
          </Button>
        </div>
        {/* PPC-F2 (свод tails-sweep-2, заход 5) — подпись говорит то, что произойдёт.
            Смена видимости это отдельная транзакция со своим диалогом, но стоит она
            ВНУТРИ формы с общим «Сохранить изменения», и соседство создавало ложное
            обещание отката по «Отмена». Одна строка снимает его: находка и была
            описана как «лечится одной подписью». */}
        <p className={own.visibilityImmediateNote}>{m.tm_visibility_applies_immediately()}</p>
        {/* access-go-live — компания проекта: изменение = перенос (confirm-диалог).
            Заблокировано при stage ≠ planning — контракт-лок (D9). */}
        {showCompanySelect && (
          <div className={styles.fields} id="project-entity">
            <Select
              label={m.project_col_entity()}
              value={effectiveTarget ?? ''}
              options={companyOptions}
              disabled={!!lockedAfterActive}
              onChange={(e) => setTargetEntityId(e.target.value || null)}
            />
          </div>
        )}
        {/* FE-66 (Sprint 9) — single info-banner replaces 4× repeated locked hint */}
        {lockedBanner}
        {/* FE-145 (Sprint 13 polish from S12-T-5): fieldset semantic group с visible
            caption «Зафиксировано контрактом» для locked-fields. Закрывает
            UI-CRITICAL «divider не виден» + UX-MAJOR «1px недостаточен как
            mental-model separator».
            FE-149 (Sprint 13 polish from S13-UX-4): explanatory subtitle below
            caption headline — снимает нагрузку с locked-banner. */}
        {/* Дизайн-ревью фазы A (UI-1 / UX-2, оба BLOCKER) — подпись-группа
            «ЗАФИКСИРОВАНО КОНТРАКТОМ · изменения требуют change-order с клиентом»
            удалена. Она и баннер описывали ОДНИ И ТЕ ЖЕ поля разными следствиями:
            баннер — «срок переносится с обоснованием, клиент меняется через заказ»,
            подпись — «нужен change-order с клиентом». Два регулятора на одно
            правило, и пользователь не знает, какая история правдива.
            Носитель правила остаётся один — баннер выше; поля связаны с ним
            через `aria-describedby`, поэтому правило видит и скринридер (прежняя
            подпись стояла под `aria-hidden` и была видна только глазами). */}
        {/* Дизайн-ревью фазы A (UI-2, BLOCKER) — правило связано с полями программно.
            Прежняя подпись-группа стояла под `aria-hidden`: человек с экрана видел
            причину замка, скринридер — нет, и четыре недоступных поля объяснения не
            имели вовсе. `role="group"` + `aria-describedby` на строки баннера
            озвучивают правило при входе в группу. */}
        <div
          className={styles.fields}
          {...(lockedAfterActive
            ? {
                role: 'group',
                'aria-label': m.project_locked_group_label(),
                // Вводная строка идёт ПЕРВОЙ: условность «пока проект не завершён»
                // — то, ради чего строку и добавили, и без неё в списке она
                // доставалась бы только глазам.
                'aria-describedby':
                  'project-locked-lead project-locked-plan project-locked-contract',
              }
            : {})}
        >
          {/* Searchable picker (server-side q-поиск по всем контрагентам). initialOption — чтобы
              предзаполненный клиент отображался именем, а не UUID, до открытия дропдауна. */}
          <CounterpartyPicker
            label={m.project_col_customer()}
            required
            disabled={!!lockedAfterActive}
            allowCreate
            createKind="customer"
            value={watched.customer_id ?? ''}
            onChange={(v) => setValue('customer_id', v, { shouldValidate: true })}
            {...(projectQuery.data?.customer_id && projectQuery.data?.customer_name
              ? {
                  initialOption: {
                    value: projectQuery.data.customer_id,
                    label: projectQuery.data.customer_name,
                  },
                }
              : {})}
          />
          <SalesOrderPicker
            label={m.project_form_so()}
            disabled={!!lockedAfterActive}
            value={watched.sales_order_id ?? ''}
            onChange={(v) => setValue('sales_order_id', v || null, { shouldValidate: true })}
            {...(projectQuery.data?.sales_order_id && projectQuery.data?.sales_order_code
              ? {
                  initialOption: {
                    value: projectQuery.data.sales_order_id,
                    label: projectQuery.data.sales_order_code,
                  },
                }
              : {})}
          />
          {/* FE-68 (Sprint 9) — plan dates lock parity с Client/Order: locked at stage != planning */}
          <DatePicker
            label={m.project_form_planned_start()}
            disabled={!!lockedAfterActive}
            value={watched.expected_start_date ?? null}
            onChange={(next) => setValue('expected_start_date', next, { shouldValidate: true })}
          />
          <DatePicker
            label={m.project_form_planned_end()}
            disabled={!!lockedAfterActive}
            value={watched.expected_end_date ?? null}
            onChange={(next) => setValue('expected_end_date', next, { shouldValidate: true })}
          />
        </div>
        {/* FE-145 (Sprint 13 polish): visible separator + caption между locked
            и editable группами. Заменяет тонкий 1px divider из FE-126. */}
        {/* Дизайн-ревью фазы A, повторный проход (UI-2 + UX-3, обе стороны об одном):
            озвучка двух парных групп была асимметрична — запертая получила
            `role="group"` с описанием, а свободная осталась под `aria-hidden`.
            Незрячий слышал запрет и не слышал, где он кончается. Границу свободной
            части несёт та же подпись, что и для глаз. */}
        {lockedAfterActive && (
          <>
            <div className={own.groupDivider} aria-hidden="true" />
            <div>
              <div className={own.sectionCaption} id="project-editable-caption">
                {m.ui_mozhno_izmenit_seychas()}
              </div>
              <div className={own.sectionSubtitle} id="project-editable-subtitle">
                {m.ui_vnutrennie_polya_ne_vliyayut_na_obyazatelstva_pered_klien()}
              </div>
            </div>
          </>
        )}
        <div
          className={styles.fields}
          {...(lockedAfterActive
            ? {
                role: 'group',
                'aria-labelledby': 'project-editable-caption',
                'aria-describedby': 'project-editable-subtitle',
              }
            : {})}
        >
          {/* FE-67 (Sprint 9) — controlled Select replaces freeform Input (parity с create form) */}
          <Select
            label={m.project_form_project_type()}
            value={watched.project_type ?? ''}
            options={projectTypeOptions}
            placeholder={m.project_form_select_type()}
            onChange={(e) =>
              setValue('project_type', (e.target.value as ProjectTypeValue) || null, {
                shouldValidate: true,
              })
            }
          />
        </div>
        <Textarea label={m.project_form_notes()} rows={3} {...register('notes')} />
        <div className={styles.footer}>
          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate({ to: '/projects/$id', params: { id } })}
            >
              {m.common_button_cancel()}
            </Button>
            {/* FE-65 (Sprint 9) — explicit «Сохранить изменения» вместо дубля page title */}
            <Button
              type="submit"
              disabled={mutation.isPending || moveMutation.isPending || !formState.isValid}
            >
              {m.project_cta_save_changes()}
            </Button>
          </div>
        </div>
      </form>
      {/* access-go-live — подтверждение переноса: масштаб (задачи) + отвязка клиента/заказа. */}
      <ConfirmDialog
        open={moveConfirmOpen}
        title={m.project_move_confirm_title()}
        description={
          <>
            <p className={own.confirmParagraph}>
              {m.project_move_confirm_body({
                name: projectQuery.data.name,
                tasks: projectQuery.data.task_summary?.total ?? 0,
                company: companyOptions.find((o) => o.value === effectiveTarget)?.label ?? '',
              })}
            </p>
            {!!(watched.customer_id || watched.sales_order_id) && (
              <p className={own.confirmParagraphSpaced}>{m.project_move_confirm_detach_note()}</p>
            )}
          </>
        }
        confirmLabel={m.project_move_confirm_cta()}
        cancelLabel={m.common_button_cancel()}
        loading={moveMutation.isPending}
        onConfirm={() => {
          if (pendingDataRef.current) moveMutation.mutate(pendingDataRef.current);
        }}
        onCancel={() => setMoveConfirmOpen(false)}
      />
      {/* project-visibility (F2) — member-review диалог перехода видимости (F3). */}
      {visibilityTarget && (
        <VisibilityChangeDialog
          open
          projectId={id}
          target={visibilityTarget}
          pending={visibilityMut.isPending}
          onCancel={() => setVisibilityTarget(null)}
          onConfirm={(excludeUserIds) =>
            visibilityMut.mutate({
              visibility: visibilityTarget,
              exclude_user_ids: excludeUserIds,
            })
          }
        />
      )}
    </div>
  );
}
